import { observer } from 'mobx-react-lite'
import React, { Fragment, useContext, useMemo, useState } from 'react'
import { Eye, Coin, Cursor, Display, Flag, GraphUp, Instagram, Megaphone, PieChart, Tiktok, Youtube, EyeSlash, Linkedin, Pinterest, Headphones, Airplane, People } from 'react-bootstrap-icons'
import { useParams } from 'react-router-dom'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import axiosService from '../../../../../utils/AxiosService'
import { currencyFormat, separateThousands } from '../../../../../utils/Helper'
import DropdownButton from '../../../../FormComponents/DropdownButton'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import CampaignTargetsUpdateModal from '../OverviewParts/CampaignTargetsUpdateModal'
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext'

export default observer(function Overview({ campaignScenario, readOnly = false, campaign: campaignProp }) {
  const { id } = useParams()
  const archived = useReadOnly()
  const noEdit = readOnly || archived
  const campaignContext = useContext(CampaignContext)
  const campaign = campaignProp ?? campaignContext?.campaign
  const [open, setOpen] = useState(true)
  const [showUpdateTargetsModal, setShowUpdateTargetsModal] = useState(false)

  const actions = campaignScenario.actions.filter(action => {
    // Check if the action's campaign scenario action group is visible
    const actionGroup = campaignScenario.campaign_scenario_action_groups.find(
      ag => ag.id === action.campaign_scenario_action_group_id
    )
    return actionGroup ? actionGroup.visible_in_budget : true
  })

  const instagramReelsCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_reel').length, [actions])
  const instagramStoriesCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_story').length, [actions])
  const instagramPostsCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_post').length, [actions])
  const instagramCarouselPostsCount = useMemo(() => actions.filter(action => action.action_type === 'instagram_carousel_post').length, [actions])
  const tiktokVideosCount = useMemo(() => actions.filter(action => action.action_type === 'tiktok_video').length, [actions])
  const youtubeVideosCount = useMemo(() => actions.filter(action => action.action_type === 'youtube_video').length, [actions])
  const youtubeShortsCount = useMemo(() => actions.filter(action => action.action_type === 'youtube_short').length, [actions])
  const linkedinBeitragCount = useMemo(() => actions.filter(action => action.action_type === 'linkedin_post').length, [actions])
  const linkedinNewsletterCount = useMemo(() => actions.filter(action => action.action_type === 'linkedin_newsletter').length, [actions])
  const linkedinVideoCount = useMemo(() => actions.filter(action => action.action_type === 'linkedin_video').length, [actions])
  const pinterestPinCount = useMemo(() => actions.filter(action => action.action_type === 'pinterest_pin').length, [actions])
  const podcastEpisodeCount = useMemo(() => actions.filter(action => action.action_type === 'podcast_episode').length, [actions])
  const totalInfluencerPayout = useMemo(() => actions.reduce((result, action) => result + action.influencer_payout_price, 0), [actions])
  const totalInfluencerPrice = useMemo(() => actions.reduce((result, action) => result + action.influencerPrice, 0), [actions])
  const totalKsaPrice = useMemo(() => actions.reduce((result, action) => result + action.totalKsaPrice, 0), [actions])
  const totalAdvertiserPrice = useMemo(() => actions.reduce((result, action) => result + (Number(action.totalAdvertiserPrice) || 0), 0), [actions])
  const totalMediaOnlyKsaPrice = useMemo(() => actions.reduce((result, action) => result + action.ksaPrice, 0), [actions])
  const totalPayoutRaw = useMemo(
    () => actions.reduce((result, action) => result + action.influencer_payout_price + (action.buyout ? action.buyout_price : 0), 0),
    [actions]
  )
  // Marge in EUR = Kosten Advertiser - media-only KSA - raw Payout: media KSA is neutral (added then subtracted),
  // Buyout KSA is not subtracted so it stays as extra margin - matches the Excel budget tool.
  const advertiserMarginPrice = useMemo(
    () => totalAdvertiserPrice - totalMediaOnlyKsaPrice - totalPayoutRaw,
    [totalAdvertiserPrice, totalMediaOnlyKsaPrice, totalPayoutRaw]
  )
  const totalReach = useMemo(() => actions.reduce((result, action) => result + action.reach, 0), [actions])
  const totalCampaignImpressions = useMemo(() => actions.reduce((result, action) => result + (action.impression ? +action.impression : 0), 0), [actions])
  const cpm = useMemo(() => totalReach ? (totalAdvertiserPrice / totalReach) * 1000 : 0, [totalAdvertiserPrice, totalReach])
  const safeCurrency = (value, symbol) => currencyFormat((Number.isFinite(Number(value)) ? Number(value) : 0).toFixed(2), symbol)

  const targetBudget = (() => {
    const a = Number(String(campaign?.influencer_budget ?? '').replace(',', '.')) || 0
    const b = Number(String(campaign?.media_budget ?? '').replace(',', '.')) || 0
    return a + b
  })()

  const targetCpm = useMemo(() => {
    const target = Number(campaign?.gross_reach_target)
    if (!Number.isFinite(target) || target <= 0) return 0
    const price = Number(totalAdvertiserPrice)
    if (!Number.isFinite(price)) return 0
    return (price / target) * 1000
  }, [totalAdvertiserPrice, campaign?.gross_reach_target])

  const parseTarget = (v) => {
    if (v == null || v === '') return 0
    return Number(String(v).replace(',', '.')) || 0
  }

  const ADVERTISER_FEE_RATE = 0.20
  const KSA_RATE = 0.049

  // Optional cost widgets (Travel Expenses / Event Attendance): "current" sums the matching
  // system-key Sonstiges field's entries across all influencers/slots for this scenario.
  const systemCostFieldTotal = (systemKey) => {
    const field = (campaign?.custom_fields || []).find(f => f.system_key === systemKey)
    if (!field) return 0
    const records = (campaignScenario?.campaign_custom_field_values || []).filter(
      v => Number(v.campaign_custom_field_id) === Number(field.id)
    )
    return records.reduce((sum, record) => {
      const raw = (Number(record?.quantity) || 0) * (Number(record?.unit_price) || 0)
      const fee = field.advertiser_fee_enabled ? raw * ADVERTISER_FEE_RATE : 0
      const ksa = field.ksa_enabled ? raw * KSA_RATE : 0
      return sum + raw + fee + ksa
    }, 0)
  }

  const travelCostTarget = parseTarget(campaign?.travel_cost_target)
  const travelCostCurrent = useMemo(
    () => systemCostFieldTotal('travel_expenses'),
    [campaign?.custom_fields, campaignScenario?.campaign_custom_field_values]
  )
  const attendanceCostTarget = parseTarget(campaign?.attendance_cost_target)
  const attendanceCostCurrent = useMemo(
    () => systemCostFieldTotal('event_attendance'),
    [campaign?.custom_fields, campaignScenario?.campaign_custom_field_values]
  )

  const calculateColor = (current, target, greenPositive = true) => {
    const t = Number(target)
    const c = Number(current)
    if (!Number.isFinite(c)) return 'text-gray-400'
    if (!Number.isFinite(t) || t === 0) return 'text-gray-400'
    const difference = Math.abs(t - c)
    if ((difference / t) * 100 < 10) return 'text-amber-500'
    return greenPositive ? (t < c ? 'text-rb-green' : 'text-red-500') : (t > c ? 'text-rb-green' : 'text-red-500')
  }

  const reachColor = calculateColor(Number(totalReach), parseTarget(campaign?.gross_reach_target))
  const impressionsColor = calculateColor(Number(totalCampaignImpressions), parseTarget(campaign?.impressions_target))
  const infPayoutBudgetColor = calculateColor(Number(totalAdvertiserPrice), Number(targetBudget), false)
  const cpmColor = calculateColor(Number(cpm), Number(targetCpm), false)
  const travelCostColor = calculateColor(travelCostCurrent, travelCostTarget, false)
  const attendanceCostColor = calculateColor(attendanceCostCurrent, attendanceCostTarget, false)

  const toggleIsVisible = () => {
    axiosService.patch(`/api/project_manager/campaigns/${id}`, { campaign: { budget_table_visible: !campaign.budget_table_visible }})
    .then(updatedCampaign => {
      campaign.budget_table_visible = !!updatedCampaign.budget_table_visible
      toastSuccess(campaign.budget_table_visible ? 'Budget table visible for advertiser' : 'Budget table hidden from advertiser')
    })
    .catch(({ data: { error } }) => toastError(error))
  }

  const scopeMetrics = useMemo(() => [
    { icon: Instagram, iconClass: 'text-pink-600 dark:text-pink-400', label: 'Instagram Reel', count: instagramReelsCount },
    { icon: Instagram, iconClass: 'text-pink-600 dark:text-pink-400', label: 'Instagram Post', count: instagramPostsCount },
    { icon: Instagram, iconClass: 'text-pink-600 dark:text-pink-400', label: 'Instagram Carousel Post', count: instagramCarouselPostsCount },
    { icon: Instagram, iconClass: 'text-pink-600 dark:text-pink-400', label: 'Instagram Story', count: instagramStoriesCount },
    { icon: Tiktok, iconClass: 'dark:text-white', label: 'Tiktok Video', count: tiktokVideosCount },
    { icon: Youtube, iconClass: 'text-red-500 dark:text-red-400', label: 'Youtube Video', count: youtubeVideosCount },
    { icon: Youtube, iconClass: 'text-red-500 dark:text-red-400', label: 'Youtube Shorts', count: youtubeShortsCount },
    { icon: Linkedin, iconClass: 'text-rb-linkedin dark:text-blue-400', label: 'LinkedIn Beitrag', count: linkedinBeitragCount },
    { icon: Linkedin, iconClass: 'text-rb-linkedin dark:text-blue-400', label: 'LinkedIn Newsletter', count: linkedinNewsletterCount },
    { icon: Linkedin, iconClass: 'text-rb-linkedin dark:text-blue-400', label: 'LinkedIn Video', count: linkedinVideoCount },
    { icon: Pinterest, iconClass: 'text-red-700 dark:text-red-400', label: 'Pinterest Pin', count: pinterestPinCount },
    { icon: Headphones, iconClass: 'dark:text-white', label: 'Podcast Episode', count: podcastEpisodeCount },
  ].filter(({ count }) => count > 0), [
    instagramReelsCount, instagramPostsCount, instagramCarouselPostsCount, instagramStoriesCount,
    tiktokVideosCount, youtubeVideosCount, youtubeShortsCount,
    linkedinBeitragCount, linkedinNewsletterCount, linkedinVideoCount, pinterestPinCount, podcastEpisodeCount,
  ])

  return (
    <Fragment>
      { campaignScenario &&
        <div className="bg-white dark:bg-rb-dark-mode rounded-lg p-4 border-1 border-border-primary dark:border-rb-dark-mode-lighter">
          <div className="flex justify-between w-full mb-4">
            <p className="font-bold text-xl dark:text-white">Overview</p>
            <div>
              {!noEdit && (
                <button
                  onClick={toggleIsVisible}
                  className="bg-rb-green text-white transition-all p-2 rounded-md mr-2 dark:text-rb-dark-mode">
                  {campaign.budget_table_visible ? <Eye /> : <EyeSlash />}
                </button>
              )}
              <DropdownButton dropdownOpen={open} setDropdownOpen={setOpen} className="hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter text-black dark:text-white transition-all p-2 rounded-md" />
            </div>
          </div>
          {open &&
            <div>
              <div className="border-1 rounded-xl border-border-primary dark:border-rb-dark-mode-lighter mb-4 p-4">
                <div className="flex items-center text-gray-400 dark:text-gray-500">
                  <Megaphone />
                  <p className="text-rb-lighter-grey dark:text-gray-400 ml-2 mb-1">Scope</p>
                </div>
                {scopeMetrics.length > 0 ? (
                  <div
                    className="gap-4"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
                  >
                    {scopeMetrics.map(({ icon: Icon, iconClass, label, count }) => (
                      <div
                        key={label}
                        className="flex justify-between border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 min-w-0"
                      >
                        <div className="flex items-center min-w-0">
                          <Icon className={`shrink-0 ${iconClass}`} />
                          <p className="ml-2 text-rb-mat-grey dark:text-white truncate">{label}</p>
                        </div>
                        <p className="font-bold dark:text-white shrink-0 ml-1">{count}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-rb-lighter-grey dark:text-gray-400 text-sm">No actions added to this campaign yet.</p>
                )}
              </div>
              <div className="border-1 rounded-xl border-border-primary dark:border-rb-dark-mode-lighter mb-4 p-4">
                <div className="flex items-center text-gray-400 dark:text-gray-500">
                  <PieChart />
                  <p className="text-rb-lighter-grey dark:text-gray-400 ml-2 mb-1">Overall Cost Overview</p>
                </div>
                <div className={`grid ${readOnly ? 'grid-cols-2' : 'grid-cols-6'} justify-between gap-4`}>
                  {!readOnly && (
                    <Fragment>
                      <div className="flex justify-between w-full border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 col-span-3">
                        <div className="flex items-center">
                          <p className="ml-2 text-rb-mat-grey dark:text-white">Total Influencer Pay Out</p>
                        </div>
                        <p className="font-bold dark:text-white">{currencyFormat(totalInfluencerPayout.toFixed(2), campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between w-full border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 col-span-3">
                        <div className="flex items-center">
                          <p className="ml-2 text-rb-mat-grey dark:text-white">Total Influencer Price</p>
                        </div>
                        <p className="font-bold dark:text-white">{currencyFormat(totalInfluencerPrice.toFixed(2), campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between w-full border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 col-span-2">
                        <div className="flex items-center">
                          <p className="ml-2 text-rb-mat-grey dark:text-white">Total KSA Tax </p>
                        </div>
                        <p className="font-bold dark:text-white">{currencyFormat(totalKsaPrice.toFixed(2), campaign.currency_symbol)}</p>
                      </div>
                    </Fragment>
                  )}
                  <div className={`flex justify-between w-full border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 ${readOnly ? '' : 'col-span-2'}`}>
                    <div className="flex items-center">
                      <p className="ml-2 text-rb-mat-grey dark:text-white">Total Advertiser Price</p>
                    </div>
                    <p className="font-bold dark:text-white">{currencyFormat(totalAdvertiserPrice.toFixed(2), campaign.currency_symbol)}</p>
                  </div>
                  <div className={`flex justify-between w-full border-1 rounded-lg border-border-primary dark:border-rb-dark-mode-lighter p-2 ${readOnly ? '' : 'col-span-2'}`}>
                    <div className="flex items-center">
                      <p className="ml-2 text-rb-mat-grey dark:text-white">Advertiser Margin</p>
                    </div>
                    <p className="font-bold dark:text-white">{currencyFormat(advertiserMarginPrice.toFixed(2), campaign.currency_symbol)}</p>
                  </div>
                </div>
              </div>
              <div className="border-1 rounded-xl border-border-primary dark:border-rb-dark-mode-lighter mb-4 p-4">
                <div className="flex items-center text-gray-400 dark:text-gray-500 justify-between">
                  <div className="flex items-center">
                    <Flag />
                    <p className="text-rb-lighter-grey dark:text-gray-400 ml-2 mb-1">Target and Status</p>
                  </div>
                  {!noEdit && (
                    <button
                      onClick={() => setShowUpdateTargetsModal(true)}
                      className='text-rb-green dark:text-rb-green ml-2 underline'>edit targets</button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-4">

                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <GraphUp className="mr-2 dark:text-white"/>
                      <p className="font-bold dark:text-white">Reach</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white">{separateThousands(campaign.gross_reach_target)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white">{separateThousands(totalReach)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${reachColor}`}>{separateThousands(Math.abs(campaign.gross_reach_target- totalReach))}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <GraphUp className="mr-2 dark:text-white"/>
                      <p className="font-bold dark:text-white">Impressions</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white">{separateThousands(campaign.impressions_target)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white">{separateThousands(totalCampaignImpressions)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${impressionsColor}`}>{separateThousands(Math.abs(campaign.impressions_target - totalCampaignImpressions))}</p>
                    </div>
                  </div>

                  <div key={`budget-${targetBudget}-${totalAdvertiserPrice}`} className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <Coin className="mr-2 dark:text-white"/>
                      <span className="font-bold text-md dark:text-white">Influencer Payout & Buyouts</span>
                      <span className="font-bold text-xs dark:text-white ml-2">(incl. fees)</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white text-right">{safeCurrency(targetBudget, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white text-right">{safeCurrency(totalAdvertiserPrice, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${infPayoutBudgetColor} text-right`}>{safeCurrency(Math.abs(targetBudget - totalAdvertiserPrice), campaign.currency_symbol)}</p>
                    </div>
                  </div>

                  {campaign.travel_cost_enabled && (
                    <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                      <div className="flex items-center mb-4">
                        <Airplane className="mr-2 dark:text-white"/>
                        <p className="font-bold dark:text-white">Travel Expenses</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(travelCostTarget, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(travelCostCurrent, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                        <p className={`font-bold ${travelCostColor} text-right`}>{safeCurrency(Math.abs(travelCostTarget - travelCostCurrent), campaign.currency_symbol)}</p>
                      </div>
                    </div>
                  )}

                  {campaign.attendance_cost_enabled && (
                    <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                      <div className="flex items-center mb-4">
                        <People className="mr-2 dark:text-white"/>
                        <p className="font-bold dark:text-white">Event Attendance</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(attendanceCostTarget, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between mb-2">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                        <p className="font-bold dark:text-white text-right">{safeCurrency(attendanceCostCurrent, campaign.currency_symbol)}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                        <p className={`font-bold ${attendanceCostColor} text-right`}>{safeCurrency(Math.abs(attendanceCostTarget - attendanceCostCurrent), campaign.currency_symbol)}</p>
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg border-1 border-border-primary dark:border-rb-dark-mode-lighter p-2">
                    <div className="flex items-center mb-4">
                      <Cursor className="mr-2 dark:text-white"/>
                      <p className="font-bold dark:text-white">CPM Advertiser</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Target</p>
                      <p className="font-bold dark:text-white text-right">{safeCurrency(targetCpm, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between mb-2">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Current</p>
                      <p className="font-bold dark:text-white text-right">{safeCurrency(cpm, campaign.currency_symbol)}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-rb-lighter-grey dark:text-gray-400">Difference</p>
                      <p className={`font-bold ${cpmColor} text-right`}>{safeCurrency(Math.abs((targetCpm || 0) - (cpm || 0)), campaign.currency_symbol)}</p>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }
      {!noEdit && (
        <CampaignTargetsUpdateModal isOpen={showUpdateTargetsModal} setIsOpen={setShowUpdateTargetsModal} />
      )}
    </Fragment>
  )
})
